import { isProblemDetails, type ProblemDetails } from '@oneapp/shared';
import { z } from 'zod';

/** An API call that didn't succeed. `status` is 0 when the server couldn't be reached at all. */
export class ApiError extends Error {
  readonly status: number;
  readonly problem: ProblemDetails | undefined;
  readonly requestId: string | undefined;

  constructor(message: string, status: number, problem?: ProblemDetails, requestId?: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.problem = problem;
    this.requestId = requestId;
  }
}

const API_BASE = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/$/, '');

/** The full address of an API path, for requests that don't go through `request` (the chat stream). */
export const apiUrl = (path: string) => `${API_BASE}${path}`;

export interface RequestOptions {
  /** An explicit bearer token (the admin token on the Keys page). */
  token?: string | null;
  /** Send the signed-in person's Firebase token (see setTokenSource). */
  auth?: boolean;
  body?: unknown;
  idempotencyKey?: string;
  /** Extra headers, e.g. If-Match with the version being changed. */
  headers?: Record<string, string>;
  signal?: AbortSignal;
  responseType?: 'response';
  /** The browser cache's part: 'no-cache' asks the server again even when a cached answer is still fresh. */
  cache?: RequestCache;
}

type TokenSource = (forceRefresh: boolean) => Promise<string | null>;
let tokenSource: TokenSource | null = null;
/** Firebase App Check: proves a call comes from EkApp's own app (when the server asks for it). */
let appCheckSource: (() => Promise<string | null>) | null = null;

/** Where calls get their App Check token. Set by the sign-in layer when App Check is set up. */
export function setAppCheckSource(source: (() => Promise<string | null>) | null) {
  appCheckSource = source;
}

/** The App Check token for this call, or null. Never throws: the server says if it needs one. */
export async function appCheckHeader(): Promise<Record<string, string>> {
  const token = appCheckSource ? await appCheckSource().catch(() => null) : null;
  return token ? { 'X-Firebase-AppCheck': token } : {};
}

/** Where `auth: true` calls get the signed-in person's token. Set by the sign-in layer. */
export function setTokenSource(source: TokenSource | null) {
  tokenSource = source;
}

/** The signed-in person's token (a fresh one with `forceRefresh`), or null when signed out. */
export async function signedInToken(forceRefresh = false): Promise<string | null> {
  return tokenSource ? tokenSource(forceRefresh) : null;
}

export const newRequestId = () => `web-${crypto.randomUUID()}`;

/** The server refused the sign-in itself (not just this request): the person has to sign in again. */
export function isSignInRefused(error: unknown): boolean {
  if (!(error instanceof ApiError)) return false;
  const code = error.problem?.code;
  return (
    (error.status === 401 && (code === 'TOKEN_INVALID' || code === 'TOKEN_REVOKED' || code === 'TOKEN_MISSING')) ||
    (error.status === 403 && code === 'ACCOUNT_DISABLED')
  );
}

/**
 * Call the EkApp API and check the response against its schema.
 * Every call carries an X-Request-Id, so a problem you report can be found in the server logs.
 */
export async function request<S extends z.ZodType>(
  method: string,
  path: string,
  schema: S,
  options: RequestOptions = {},
): Promise<z.output<S>> {
  const useSignIn = Boolean(options.auth && tokenSource && options.token === undefined);
  const token = useSignIn && tokenSource ? await tokenSource(false) : (options.token ?? null);
  try {
    return await send(method, path, schema, options, token);
  } catch (error) {
    // Firebase tokens last an hour: when the server says this one expired, get a fresh one and try once more.
    if (useSignIn && tokenSource && error instanceof ApiError && error.problem?.code === 'TOKEN_EXPIRED') {
      return send(method, path, schema, options, await tokenSource(true));
    }
    throw error;
  }
}

async function send<S extends z.ZodType>(
  method: string,
  path: string,
  schema: S,
  options: RequestOptions,
  token: string | null,
): Promise<z.output<S>> {
  const requestId = newRequestId();
  const headers: Record<string, string> = {
    Accept: 'application/json',
    'X-Request-Id': requestId,
    ...(await appCheckHeader()),
  };
  if (options.body !== undefined) headers['Content-Type'] = 'application/json';
  if (token) headers.Authorization = `Bearer ${token}`;
  if (options.idempotencyKey) headers['Idempotency-Key'] = options.idempotencyKey;
  for (const [name, value] of Object.entries(options.headers ?? {})) headers[name] = value;

  let response: Response;
  try {
    response = await fetch(`${API_BASE}${path}`, {
      method,
      headers,
      ...(options.body !== undefined ? { body: JSON.stringify(options.body) } : {}),
      ...(options.signal ? { signal: options.signal } : {}),
      ...(options.cache ? { cache: options.cache } : {}),
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error;
    throw new ApiError('network', 0, undefined, requestId);
  }

  if (response.ok && options.responseType === 'response') return schema.parse(response);
  const text = await response.text();
  let data: unknown = null;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = null;
    }
  }
  const serverRequestId = response.headers.get('x-request-id') ?? requestId;
  if (!response.ok) {
    const problem = isProblemDetails(data) ? data : undefined;
    throw new ApiError(
      problem?.detail ?? problem?.title ?? `HTTP ${response.status}`,
      response.status,
      problem,
      serverRequestId,
    );
  }
  const parsed = schema.safeParse(data);
  if (!parsed.success) throw new ApiError('unexpected-response', response.status, undefined, serverRequestId);
  return parsed.data;
}

export const api = {
  get: <S extends z.ZodType>(path: string, schema: S, options?: Omit<RequestOptions, 'body'>) =>
    request('GET', path, schema, options),
  post: <S extends z.ZodType>(path: string, schema: S, options?: RequestOptions) =>
    request('POST', path, schema, options),
  patch: <S extends z.ZodType>(path: string, schema: S, options?: RequestOptions) =>
    request('PATCH', path, schema, options),
  put: <S extends z.ZodType>(path: string, schema: S, options?: RequestOptions) =>
    request('PUT', path, schema, options),
  /** A body only where the API asks for one (deleting the account: `{ confirm: "DELETE" }`). */
  delete: <S extends z.ZodType>(path: string, schema: S, options?: RequestOptions) =>
    request('DELETE', path, schema, options),
};

/** Private tool pages share sign-in refresh and API errors, including artifact downloads. */
export function privateRequest(path: string, body?: unknown) {
  return request(body === undefined ? 'GET' : 'POST', `/api/v1${path}`, z.instanceof(Response), {
    auth: true,
    body,
    responseType: 'response',
    signal: AbortSignal.timeout(10000),
  });
}
